import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CallStageChangeEvent, LiveCallSnapshot } from "@/types";

const MAX_RECENT = 20;

// Keep in sync with the `terminal` flags in @/lib/call/stage-meta (not
// imported from there to keep icon modules out of the store).
const TERMINAL_STAGES = new Set(["NO_PICKUP", "DONE", "FAILED"]);

function isTerminal(stage: LiveCallSnapshot["processing_stage"]): boolean {
  return !!stage && TERMINAL_STAGES.has(stage);
}

function toEpochMs(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const ms = new Date(v).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  return Date.now();
}

// Socket payloads are untrusted at the wire: a backend serializing agentId as
// a string or updatedAt as an ISO date would silently break the strict-equality
// filters and every Date.now()-based watchdog (NaN comparisons). Normalize at
// the store boundary.
export function normalizeSnapshot(raw: LiveCallSnapshot): LiveCallSnapshot {
  return {
    ...raw,
    callId: Number(raw.callId),
    agentId: raw.agentId == null ? null : Number(raw.agentId),
    updatedAt: toEpochMs(raw.updatedAt),
  };
}

export type LiveCallDialerState = {
  recentCalls: LiveCallSnapshot[];
  currentIndex: number;
  autoFollow: boolean;
  loading: boolean;
  error: string | null;
};

const initialState: LiveCallDialerState = {
  recentCalls: [],
  currentIndex: 0,
  autoFollow: true,
  loading: false,
  error: null,
};

const liveCallDialerSlice = createSlice({
  name: "liveCallDialer",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setRecentCalls(state, action: PayloadAction<LiveCallSnapshot[]>) {
      // Hydrates run repeatedly now (repair polling), not just on mount, so
      // merge per call instead of wholesale-replacing:
      //  - never regress a terminal stage a live event already delivered
      //    (a poll response served concurrently with the emit can still
      //    carry the older non-terminal snapshot);
      //  - keep name fields filled in by enrichSnapshot when the server
      //    snapshot lacks them (enrichedRef won't re-fetch them).
      const prevById = new Map(state.recentCalls.map((c) => [c.callId, c]));
      const merged = action.payload.map(normalizeSnapshot).map((incoming) => {
        const existing = prevById.get(incoming.callId);
        if (!existing) return incoming;
        if (isTerminal(existing.processing_stage) && !isTerminal(incoming.processing_stage)) {
          return existing;
        }
        return {
          ...incoming,
          leadName: incoming.leadName ?? existing.leadName,
          contactName: incoming.contactName ?? existing.contactName,
          contactEmail: incoming.contactEmail ?? existing.contactEmail,
          contactPhone: incoming.contactPhone ?? existing.contactPhone,
          companyName: incoming.companyName ?? existing.companyName,
          number: incoming.number ?? existing.number,
          // A hydrate carries the server's (older) timestamp; a live event may
          // have just stamped Date.now(). Never move a call's clock backwards —
          // it would reorder the stack and can push it out of the window.
          updatedAt: Math.max(existing.updatedAt ?? 0, incoming.updatedAt ?? 0),
        };
      });
      const sorted = merged
        .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
        .slice(0, MAX_RECENT);
      state.recentCalls = sorted;
      if (state.currentIndex >= sorted.length) {
        state.currentIndex = sorted.length > 0 ? 0 : 0;
      }
      state.loading = false;
      state.error = null;
    },
    applyStageChange(state, action: PayloadAction<CallStageChangeEvent>) {
      const evt = action.payload;
      const evtCallId = Number(evt.callId);
      const existingIndex = state.recentCalls.findIndex((c) => c.callId === evtCallId);
      const existing = existingIndex >= 0 ? state.recentCalls[existingIndex] : null;
      // Terminal-regression guard (same invariant as setRecentCalls): an
      // out-of-order or late event must never flip a finished call back to a
      // spinner and re-arm its watchdogs.
      if (existing && isTerminal(existing.processing_stage) && !isTerminal(evt.processing_stage)) {
        return;
      }
      const snapshot: LiveCallSnapshot = {
        callId: evtCallId,
        agentId: evt.agentId == null ? null : Number(evt.agentId),
        leadId: evt.leadId,
        leadName: evt.leadName ?? existing?.leadName ?? null,
        contactName: evt.contactName ?? existing?.contactName ?? null,
        contactEmail: evt.contactEmail ?? existing?.contactEmail ?? null,
        contactPhone: evt.contactPhone ?? existing?.contactPhone ?? null,
        companyName: evt.companyName ?? existing?.companyName ?? null,
        number: evt.number ?? existing?.number ?? null,
        processing_stage: evt.processing_stage ?? existing?.processing_stage ?? null,
        current_workflow_node: evt.current_workflow_node ?? existing?.current_workflow_node ?? null,
        stage_history: evt.stage_history ?? existing?.stage_history ?? [],
        updatedAt: Date.now(),
      };

      // Always build a brand-new array reference so any consumer relying on
      // reference equality (useSelector default, React.memo) re-renders.
      const without = existingIndex >= 0
        ? state.recentCalls.filter((_, i) => i !== existingIndex)
        : state.recentCalls;
      const merged = [snapshot, ...without].slice(0, MAX_RECENT);
      const sorted = [...merged].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
      state.recentCalls = sorted;

      if (existingIndex < 0) {
        if (state.autoFollow) {
          state.currentIndex = 0;
        } else if (state.currentIndex < sorted.length - 1) {
          state.currentIndex += 1;
        }
      } else {
        const newIndex = sorted.findIndex((c) => c.callId === evtCallId);
        if (existingIndex === state.currentIndex && newIndex !== state.currentIndex) {
          state.currentIndex = newIndex;
        }
      }
    },
    // Merge in additional fields onto an existing snapshot without touching
    // stage history or processing_stage — used by useLiveCallStages to fill
    // in contact / lead / company names that the backend snapshot didn't
    // include (e.g. when the backend hasn't been rebuilt yet).
    enrichSnapshot(state, action: PayloadAction<{ callId: number; patch: Partial<LiveCallSnapshot> }>) {
      const idx = state.recentCalls.findIndex((c) => c.callId === action.payload.callId);
      if (idx < 0) return;
      // Immutable update — new object at index, new array reference.
      state.recentCalls = state.recentCalls.map((c, i) =>
        i === idx ? { ...c, ...action.payload.patch } : c,
      );
    },
    setCurrentIndex(state, action: PayloadAction<number>) {
      const n = state.recentCalls.length;
      if (n === 0) {
        state.currentIndex = 0;
        return;
      }
      const idx = action.payload;
      state.currentIndex = Math.max(0, Math.min(n - 1, idx));
    },
    next(state) {
      if (state.recentCalls.length === 0) return;
      state.currentIndex = Math.min(state.recentCalls.length - 1, state.currentIndex + 1);
    },
    prev(state) {
      if (state.recentCalls.length === 0) return;
      state.currentIndex = Math.max(0, state.currentIndex - 1);
    },
    setAutoFollow(state, action: PayloadAction<boolean>) {
      state.autoFollow = action.payload;
    },
    clearLiveCalls(state) {
      state.recentCalls = [];
      state.currentIndex = 0;
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setLoading: setLiveCallsLoading,
  setError: setLiveCallsError,
  setRecentCalls,
  applyStageChange,
  enrichSnapshot,
  setCurrentIndex,
  next: nextLiveCall,
  prev: prevLiveCall,
  setAutoFollow,
  clearLiveCalls,
} = liveCallDialerSlice.actions;

export default liveCallDialerSlice.reducer;
